import {
  Button,
  Flex,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Table,
} from 'antd'

import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import Swal from 'sweetalert2'
import {
  deleteCommunityUser,
  getCommunityCategoryList,
  getCommunityUserList,
  getUserTicketList,
  issueUserTicket,
  registerCommunityParticipant,
} from '../../api/community.api'
import ContentHeader from '../../components/shared/ContentHeader'
import styles from '../../styles/sales.module.scss'

const CommunityUserListPage = () => {
  const [communityUserList, setCommunityUserList] = useState([])
  const [communityCategoryList, setCommunityCategoryList] = useState([])
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [totalElements, setTotalElements] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedCategoryId, setSelectedCategoryId] = useState(null)
  const [phoneInput, setPhoneInput] = useState('')
  const [isRegistering, setIsRegistering] = useState(false)
  const [isTicketModalOpen, setIsTicketModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [form] = Form.useForm()

  // 티켓 목록 모달 관련 상태
  const [isTicketListModalOpen, setIsTicketListModalOpen] = useState(false)
  const [userTicketList, setUserTicketList] = useState([])
  const [ticketPage, setTicketPage] = useState(0)
  const [ticketPageSize, setTicketPageSize] = useState(10)
  const [ticketTotalElements, setTicketTotalElements] = useState(0)
  const [isTicketListLoading, setIsTicketListLoading] = useState(false)

  const fetchCommunityUserList = async (categoryId, page = 0, size = 10) => {
    setIsLoading(true)
    try {
      const res = await getCommunityUserList(categoryId, page, size)
      setCommunityUserList(res.content)
      setTotalElements(res.totalElements)
    } catch (error) {
      console.error(error)
      setCommunityUserList([])
    } finally {
      setIsLoading(false)
    }
  }

  const fetchCommunityCategoryList = async () => {
    try {
      const res = await getCommunityCategoryList()
      setCommunityCategoryList(Array.isArray(res) ? res : [])
      if (Array.isArray(res) && res.length > 0) {
        setSelectedCategoryId(res[0].id)
      }
    } catch (error) {
      console.error(error)
      setCommunityCategoryList([])
    }
  }

  const fetchUserTicketList = async (userId, page = 0, size = 10) => {
    setIsTicketListLoading(true)
    try {
      const res = await getUserTicketList(userId, page, size)
      setUserTicketList(res.content || [])
      setTicketTotalElements(res.totalElements || 0)
    } catch (error) {
      console.error(error)
      setUserTicketList([])
      message.error('티켓 목록을 불러오는데 실패했습니다.')
    } finally {
      setIsTicketListLoading(false)
    }
  }

  const handleRegisterUser = async () => {
    const sanitized = (phoneInput || '').replace(/\D/g, '')
    if (!selectedCategoryId) {
      message.warning('카테고리를 먼저 선택해주세요.')
      return
    }
    if (!sanitized) {
      message.warning('휴대폰 번호를 입력해주세요.')
      return
    }

    // 휴대폰 형식 예외처리 (한국 기준: 010, 011 등 10~11자리)
    const phoneRegex = /^01[016789]\d{7,8}$/
    if (!phoneRegex.test(sanitized)) {
      message.warning('휴대폰번호 형식을 확인해주세요')
      return
    }

    setIsRegistering(true)
    try {
      await registerCommunityParticipant(selectedCategoryId, sanitized)
      message.success('유저가 등록되었습니다.')
      setPhoneInput('')
      fetchCommunityUserList(selectedCategoryId, page, pageSize)
    } catch (e) {
      if (e.response.data.errorCode === 409) {
        message.warning(e.response.data.detail[1].description)
      } else {
        message.error('유저 등록에 실패했습니다.')
      }
    } finally {
      setIsRegistering(false)
    }
  }

  useEffect(() => {
    fetchCommunityCategoryList()
  }, [])

  useEffect(() => {
    if (selectedCategoryId !== null && selectedCategoryId !== undefined) {
      fetchCommunityUserList(selectedCategoryId, page, pageSize)
    }
  }, [selectedCategoryId, page, pageSize])

  const alertUserDelete = (id) => {
    Swal.fire({
      text: '유저를 삭제하시겠습니까?',
      showCancelButton: true,
      confirmButtonText: '삭제',
      cancelButtonText: '취소',
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        handleDeleteUser(id)
      }
    })
  }

  const handleDeleteUser = async (id) => {
    // await deleteCommunityUser(id)
    try {
      await deleteCommunityUser(id)
      message.success('유저가 삭제되었습니다.')
      fetchCommunityUserList(selectedCategoryId, page, pageSize)
    } catch (e) {
      message.error('유저 삭제에 실패했습니다.')
    }
  }

  const openTicketModal = (record) => {
    setSelectedUser(record)
    const selectedCategory = communityCategoryList.find(
      (cat) => cat.id === selectedCategoryId,
    )
    form.setFieldsValue({
      userId: record.userId,
      programOptionId: selectedCategory?.programOptionId || null,
      quantity: 1,
      type: null,
    })
    setIsTicketModalOpen(true)
  }

  const handleTicketIssue = async () => {
    try {
      const values = await form.validateFields()

      // 발급 확인 팝업
      const result = await Swal.fire({
        title: '스캔권 발급 확인',
        text: `${selectedUser?.name || '사용자'}님에게 ${values.quantity}장 발급하시겠습니까?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: '발급',
        cancelButtonText: '취소',
        reverseButtons: true,
      })

      if (!result.isConfirmed) {
        return
      }

      await issueUserTicket({
        userId: values.userId,
        programOptionId: values.programOptionId,
        quantity: values.quantity,
        issueType:
          values.type === 'issuedAfterCompletion'
            ? 'issuedAfterCompletion'
            : 'new',
      })
      message.success('스캔권이 발급되었습니다.')
      setIsTicketModalOpen(false)
      form.resetFields()
      setSelectedUser(null)
    } catch (error) {
      if (error.errorFields) {
        message.warning('모든 필드를 올바르게 입력해주세요.')
      } else {
        message.error('스캔권 발급에 실패했습니다.')
      }
    }
  }

  const handleTicketModalCancel = () => {
    setIsTicketModalOpen(false)
    form.resetFields()
    setSelectedUser(null)
  }

  const openUserTicketListModal = (record) => {
    setSelectedUser(record)
    setTicketPage(0)
    setIsTicketListModalOpen(true)
    fetchUserTicketList(record.userId, 0, ticketPageSize)
  }

  const handleTicketListModalCancel = () => {
    setIsTicketListModalOpen(false)
    setSelectedUser(null)
    setUserTicketList([])
    setTicketPage(0)
    setTicketTotalElements(0)
  }

  const getStatusText = (status) => {
    switch (status) {
      case 'NOT_USED':
        return '미사용'
      case 'IN_USE':
        return '사용중'
      case 'USED':
        return '만료'
      default:
        return status
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'NOT_USED':
        return '#52c41a'
      case 'IN_USE':
        return '#1890ff'
      case 'USED':
        return '#ff4d4f'
      default:
        return '#000'
    }
  }

  const ticketColumns = [
    {
      title: '티켓 ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'center',
    },
    {
      title: '발급일시',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      align: 'center',
      render: (date) => <span style={{ fontSize: 12 }}>{date}</span>,
    },
    {
      title: '사용 시작일시',
      dataIndex: 'startDateTime',
      key: 'startDateTime',
      width: 150,
      align: 'center',
      render: (date) => <span style={{ fontSize: 12 }}>{date || '-'}</span>,
    },
    {
      title: '사용 종료일시',
      dataIndex: 'endDateTime',
      key: 'endDateTime',
      width: 150,
      align: 'center',
      render: (date) => <span style={{ fontSize: 12 }}>{date || '-'}</span>,
    },
    {
      title: '상태',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center',
      render: (status) => (
        <span
          style={{
            color: getStatusColor(status),
            fontWeight: 'bold',
            fontSize: 12,
          }}
        >
          {getStatusText(status)}
        </span>
      ),
    },
  ]

  const columns = [
    // {
    //   title: 'ID',
    //   dataIndex: 'id',
    //   key: 'id',
    //   width: 70,
    //   align: 'center',
    // },
    {
      title: '기수(등록일 기준)',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      align: 'center',
      render: (date) => (
        <div
          style={{
            fontSize: 12,
            fontWeight: 'bold',
            backgroundColor:
              dayjs(date) >= dayjs('20250903') ? '#c7e5ff' : '#ffc5c5',
            padding: '4px 12px',
            borderRadius: 60,
            width: 'fit-content',
            margin: '0 auto',
          }}
        >
          {dayjs(date) >= dayjs('20250903') ? '1.5기' : '1기'}
        </div>
      ),
    },
    {
      title: '이름',
      dataIndex: 'name',
      key: 'name',
      width: 100,
      align: 'center',
    },
    {
      title: '유저 아이디',
      dataIndex: 'userId',
      key: 'userId',
      width: 100,
      align: 'center',
    },
    {
      title: '전화번호',
      dataIndex: 'phone',
      key: 'phone',
      width: 160,
      align: 'center',
    },
    {
      title: '사용자 등록일',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      align: 'center',
      render: (date) => <span style={{ fontSize: 12 }}>{date}</span>,
    },
    // {
    //   title: '댓글 관리',
    //   dataIndex: 'id',
    //   key: 'id',
    //   width: 100,
    //   align: 'center',
    //   render: (id) => <Button type="link">댓글관리</Button>,
    // },
    {
      title: '유저 삭제',
      dataIndex: 'id',
      key: 'id',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Button
          type="link"
          style={{ color: 'red' }}
          onClick={() => alertUserDelete(record.id)}
        >
          삭제
        </Button>
      ),
    },
    {
      title: '스캔권 발급',
      dataIndex: 'id',
      key: 'id',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Flex gap={10} justify="center">
          <Button type="default" onClick={() => openTicketModal(record)}>
            발급 관리
          </Button>
          <Button
            type="default"
            onClick={() => openUserTicketListModal(record)}
          >
            보유 티켓 목록
          </Button>
        </Flex>
      ),
    },
  ]

  const categoryOptions = (
    Array.isArray(communityCategoryList) ? communityCategoryList : []
  ).map((cat) => ({ label: cat.categoryName, value: cat.id }))

  return (
    <>
      <ContentHeader title="커뮤니티 참여자 관리" />
      <div
        className={`${styles['product-register-wrap']}`}
        style={{ minWidth: '1024px' }}
      >
        <Flex
          justify="space-between"
          align="center"
          style={{
            background: '#f8fafd',
            borderRadius: 10,
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            padding: '18px 24px',
            marginTop: 24,
            marginBottom: 18,
            gap: 16,
          }}
        >
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <h3>📢 커뮤니티 체험단 선택</h3>
            <Select
              style={{ width: 280 }}
              value={selectedCategoryId}
              options={categoryOptions}
              onChange={(value) => {
                setSelectedCategoryId(value)
                setPage(0)
              }}
              placeholder="카테고리 선택"
            />
            <Input
              style={{ width: 200 }}
              placeholder="휴대폰 번호 (숫자만)"
              value={phoneInput}
              maxLength={13}
              onChange={(e) => setPhoneInput(e.target.value.replace(/\D/g, ''))}
              onPressEnter={handleRegisterUser}
              disabled={!selectedCategoryId}
            />
            <Button
              type="primary"
              onClick={handleRegisterUser}
              loading={isRegistering}
              disabled={!selectedCategoryId || !phoneInput}
            >
              등록
            </Button>
          </div>
          <span style={{ fontWeight: 600, fontSize: 18, color: '#1677ff' }}>
            총{' '}
            <span style={{ fontWeight: 'bold', fontSize: 22 }}>
              {totalElements}
            </span>{' '}
            명
          </span>
        </Flex>

        <Row
          className={`${styles['product-register-border-box']}`}
          wrap={false}
          style={{ marginTop: 20, borderBottom: 0 }}
        >
          <span className={`${styles['product-register-box-title']}`}>
            커뮤니티 유저 목록
          </span>
        </Row>
        <div
          className={`${styles['product-register-border-box']}`}
          style={{ padding: '20px 25px 40px', height: 'fit-content' }}
        >
          <Table
            dataSource={communityUserList}
            rowKey="id"
            columns={columns}
            loading={isLoading}
            pagination={{
              current: page + 1,
              pageSize: pageSize,
              total: totalElements,
              onChange: (p, ps) => {
                setPage(p - 1)
                setPageSize(ps)
              },
              showSizeChanger: true,
            }}
            bordered
          />
        </div>

        {/* 스캔권 발급 관리 모달 */}
        <Modal
          title="스캔권 발급 관리"
          open={isTicketModalOpen}
          onOk={handleTicketIssue}
          onCancel={handleTicketModalCancel}
          okText="발급"
          cancelText="취소"
          width={500}
        >
          <Form form={form} layout="vertical" style={{ marginTop: 20 }}>
            <Form.Item label="유저 아이디" name="userId">
              <Input disabled />
            </Form.Item>

            <Form.Item label="프로그램 옵션 ID" name="programOptionId">
              <Input disabled />
            </Form.Item>

            <Form.Item
              label="발급 장수"
              name="quantity"
              rules={[
                { required: true, message: '발급 장수를 입력해주세요.' },
                {
                  type: 'number',
                  min: 1,
                  message: '1 이상의 숫자를 입력해주세요.',
                },
              ]}
            >
              <InputNumber
                min={1}
                style={{ width: '100%' }}
                placeholder="발급할 스캔권 수량을 입력하세요"
              />
            </Form.Item>

            <Form.Item
              label="타입"
              name="type"
              rules={[{ required: true, message: '타입을 선택해주세요.' }]}
            >
              <Select placeholder="발급 타입을 선택하세요">
                <Select.Option value="issuedAfterCompletion">
                  사용완료 처리 후 발급
                </Select.Option>
                <Select.Option value={'new'}>새로 발급</Select.Option>
              </Select>
            </Form.Item>
          </Form>
        </Modal>

        {/* 유저 보유 티켓 목록 모달 */}
        <Modal
          title={`유저 보유 티켓 목록 - ${selectedUser?.name || '사용자'}`}
          open={isTicketListModalOpen}
          onCancel={handleTicketListModalCancel}
          footer={[
            <Button key="close" onClick={handleTicketListModalCancel}>
              닫기
            </Button>,
          ]}
          width={800}
        >
          <div style={{ width: '100%' }}>
            <p
              style={{
                textAlign: 'right',
                marginTop: 24,
                marginBottom: 16,
                fontWeight: 600,
              }}
            >
              총
              <span style={{ fontWeight: 'bold', fontSize: 20 }}>
                {' '}
                {ticketTotalElements}
              </span>{' '}
              장
            </p>
            <Table
              dataSource={userTicketList}
              rowKey="id"
              columns={ticketColumns}
              loading={isTicketListLoading}
              pagination={{
                current: ticketPage + 1,
                pageSize: ticketPageSize,
                total: ticketTotalElements,
                onChange: (p, ps) => {
                  const newPage = p - 1
                  setTicketPage(newPage)
                  setTicketPageSize(ps)
                  fetchUserTicketList(selectedUser?.userId, newPage, ps)
                },
                // showSizeChanger: true,
                // showTotal: (total, range) =>
                //   `${range[0]}-${range[1]} of ${total} items`,
              }}
              bordered
              size="small"
            />
          </div>
        </Modal>
      </div>
    </>
  )
}

export default CommunityUserListPage
